import { useState } from "react";
import { toast } from "sonner";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ShoppingBag, Loader2 } from "lucide-react";

export default function ShopLogin() {
  const nav = useNavigate();
  const location = useLocation();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async () => {
    if (!mobile || !password) { toast.error("Enter mobile and password."); return; }
    setBusy(true);
    try {
      await api.post("/store/login", { mobile, password });
      toast.success("Welcome back!");
      // Honour `?next=/some/path` so the shopper lands back on the screen
      // they came from (e.g. the Help FAB re-opens after login).
      const params = new URLSearchParams(location.search);
      const next = params.get("next");
      const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : "/shop";
      nav(safeNext);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  const openForgot = () => {
    setForgotEmail("");
    setForgotSent(false);
    setForgotOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      // Response is always 200 to avoid leaking account existence.
      setForgotSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-2">
            <ShoppingBag className="w-6 h-6 text-[var(--shop-primary,#0055FF)]" />
            <span className="font-heading text-xl font-bold">DialFlow Shop</span>
          </div>
          <h1 className="font-heading text-2xl font-bold">Log in</h1>
        </div>
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-5 space-y-3">
            <div className="space-y-2">
              <Label>Mobile number</Label>
              <Input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="+91 98765 43210"
                data-testid="shop-login-mobile"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Password</Label>
                <button
                  type="button"
                  onClick={openForgot}
                  className="text-xs text-[var(--shop-primary,#0055FF)] hover:underline"
                  data-testid="shop-login-forgot"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                data-testid="shop-login-password"
              />
            </div>
            <Button
              onClick={submit}
              disabled={busy}
              className="w-full bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)] h-11"
              data-testid="shop-login-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Log in
            </Button>
            <div className="text-center text-xs text-slate-500 pt-1">
              New here?{" "}
              <Link to="/shop/signup" className="text-[var(--shop-primary,#0055FF)] hover:underline">
                Create an account
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forgot-password magic-link modal */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-sm" data-testid="shop-forgot-dialog">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Enter the email you added to your account and we&apos;ll send a
              one-time link to set a new password. Didn&apos;t add an email
              at signup? Contact the shop to have your password reset.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-4 space-y-3">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
                If <span className="font-medium">{forgotEmail}</span> matches
                an account, a reset link is on its way. Check your inbox (and
                spam) in the next couple of minutes.
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setForgotOpen(false)}
                  className="w-full bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)]"
                  data-testid="shop-forgot-close"
                >
                  Got it
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="shopForgotEmail">Email</Label>
                <Input
                  id="shopForgotEmail"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="shop-forgot-email"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)]"
                  data-testid="shop-forgot-submit"
                >
                  {forgotLoading ? "Sending…" : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
